/**
 * BLOG LISTING PAGE
 *
 * @returns {Promise<void>}
 */
const main = async () => {
   const [
      {default: Vue},
      {ModalPlugin},
      {AjaxMixin},
      {ValidationProvider, ValidationObserver},
      VueScrollTo,
      {default: BlogArticleListItem},
      {default: CategoriesMenu},
      {default: BlogMixin},
   ] = await Promise.all([
      import(/* webpackChunkName: "vue" */ 'vue'),
      import(/* webpackChunkName: "bs-modal" */ 'bootstrap-vue'),
      import(/* webpackChunkName: "global-mixins-ajax" */ '@mymyMixins/ajax'),
      import(/* webpackChunkName: "vee-validate" */ 'vee-validate'),
      import(/* webpackChunkName: "vue-scrollto" */ 'vue-scrollto'),
      import(/* webpackChunkName: "blog-article-list-item" */ '@/js/site/blog/components/BlogArticleListItem'),
      import(/* webpackChunkName: "blog-categories-menu" */ '@/js/site/blog/components/CategoriesMenu'),
      import(/* webpackChunkName: "blog-pages-mixin" */ '@/js/site/blog/_pages-mixin'),
   ]);

   // for scrolling
   Vue.use(VueScrollTo, {
      offset: -110,
   });

   // use Bootstrap-Vue components
   Vue.use(ModalPlugin);

   const DEFAULT_ENTRY_LIMIT = 10;
   const VIEW_MODE_POST_DATE = 'postDate';
   const VIEW_MODE_SEARCH = 'search';
   const VIEW_MODE_CATEGORY = 'category';

   // Create our vue instance
   const vm = new Vue({
      name: 'BlogListingPage',
      el: '#blog-listing',
      delimiters: ['[[', ']]'],
      mixins: [AjaxMixin, BlogMixin],
      components: {
         'validation': ValidationProvider,
         'validation-observer': ValidationObserver,
         'article-list-item': BlogArticleListItem,
         'categories-menu': CategoriesMenu,
      },
      data: {
         lists: {
            postDate: {
               active: false,
               offset: 0,
               entries: [],
            },
            search: {
               active: false,
               offset: 0,
               query: '',
               field: '',
            },
            category: {
               active: false,
               offset: 0,
               id: null,
               title: '',
            },
         },
         entries: [],
      },
      mounted() {
         // reuse categories we've already queried for and passed to Vue
         if (this.$el.dataset.categories !== '') {
            this.categories = JSON.parse(this.$el.dataset.categories);
         }

         // if no pre-fetched categories, call our controller
         if (! this.categories) {
            this.makeGetRequest('mymy/settings/blog-categories').then((response) => this.categories = response.data);
         }

         // reuse entries we've already queried for and passed to Vue
         if (this.$el.dataset.entries !== '') {
            this.entries = JSON.parse(this.$el.dataset.entries);
         }

         // figure out which view mode we are in and if we need to call our controller for entries
         if (this.$el.dataset.query) {
            // SEARCH
            this.lists.search.query = this.$el.dataset.query;
            this.lists.search.field = this.$el.dataset.query;
            this.switchTo(VIEW_MODE_SEARCH);
            if (this.entries) {
               ++this.lists.search.offset;
            }
            else {
               this.filterBySearchQuery(false);
            }
         }
         else if (this.$el.dataset.cat) {
            // CATEGORY
            this.switchTo(VIEW_MODE_CATEGORY);
            if (this.entries) {
               ++this.lists.category.offset;
               this.lists.category.id = parseInt(this.$el.dataset.cat);
               for (let category of this.categories) {
                  if (category.id == this.lists.category.id) {
                     this.lists.category.title = category.title;
                     break;
                  }
               }
            }
            else {
               this.filterByCategory(parseInt(this.$el.dataset.cat), false);
            }
         }
         else {
            // POST-DATE
            this.switchTo(VIEW_MODE_POST_DATE);
            if (this.entries) {
               ++this.lists.postDate.offset;
            }
            else {
               this.orderByPostDate(false);
            }
         }
      },
      methods: {
         /**
          * Switch to a different mode for viewing the list of articles.
          *
          * @param mode
          */
         switchTo(mode) {
            switch (mode) {
               case VIEW_MODE_POST_DATE:
                  this.lists.category.id = null;
                  this.lists.category.title = '';
                  this.lists.search.query = '';
                  this.lists.search.field = '';
                  break;
               case VIEW_MODE_SEARCH:
                  this.lists.category.id = null;
                  this.lists.category.title = '';
                  break;
               case VIEW_MODE_CATEGORY:
                  this.lists.search.query = '';
                  this.lists.search.field = '';
                  break;
               default:
                  return;
            }
            for (let md of [VIEW_MODE_POST_DATE, VIEW_MODE_SEARCH, VIEW_MODE_CATEGORY]) {
               this.lists[md].active = false;
            }
            this.lists[mode].active = true;
         },

         /**
          * View all blog articles listed by their post date, older dates towards the bottom.
          * This is the default blog articles list.
          *
          * @param loading {boolean}
          * @return {Promise<void>}
          */
         async orderByPostDate(loading = true) {
            if (loading) {
               this.modals.loading.show = true;
            }

            if (this.lists.postDate.entries.length) {
               this.entries = this.lists.postDate.entries;
            }
            else {
               this.entries = this.lists.postDate.entries = await this.getArticles(DEFAULT_ENTRY_LIMIT);
            }

            this.switchTo('postDate');

            if (loading) {
               this.modals.loading.show = false;
            }
         },

         /**
          * Search for blog entries based on the user's query inputted into the search form.
          *
          * @param loading {boolean}
          * @return {Promise<void>}
          */
         async filterBySearchQuery(loading = true) {
            if (typeof this.lists.search.field !== 'string') {
               return;
            }

            if (loading) {
               this.modals.loading.show = true;
            }

            if (this.lists.search.field === '') {
               // if the user searches with an empty string, load all the blog articles
               await this.orderByPostDate();
            }
            else {
               this.entries = await this.getArticles(
                  DEFAULT_ENTRY_LIMIT,
                  this.lists.search.offset,
                  this.lists.search.field
               );
               this.switchTo('search');
               this.lists.search.query = this.lists.search.field;
            }

            if (loading) {
               this.modals.loading.show = false;
            }
         },

         /**
          * Show only blog articles belonging to a selected category.
          *
          * @param id {number}
          * @param loading {boolean}
          * @return {Promise<void>}
          */
         async filterByCategory(id, loading = false) {
            if (typeof id !== 'number') {
               return;
            }

            if (id === this.lists.category.id) {
               return;
            }

            if (loading) {
               this.modals.loading.show = true;
            }

            this.lists.category.id = id;
            this.entries = await this.getArticles(DEFAULT_ENTRY_LIMIT, null, null, id);
            this.switchTo('category');

            for (let category of this.categories) {
               if (category.id == id) {
                  this.lists.category.title = category.title;
                  break;
               }
            }

            if (loading) {
               this.modals.loading.show = false;
            }
         },

         /**
          * Get an array of blog article entries from our controller.
          *
          * @param limit {number}
          * @param offset {number}
          * @param query {string}
          * @param categoryId {number}
          * @return {Promise<*>}
          */
         async getArticles(limit = null, offset = null, query = null, categoryId = null) {

            const response = await this.makePostRequest('mymy/settings/blog-listing-entries', {
               limit: limit ?? DEFAULT_ENTRY_LIMIT,
               offset: offset,
               q: query,
               cat: categoryId,
            });

            return response.data;
         },
      }
   });

   return vm;
};

// Execute async function
main().then( (vm) => {
});

// Accept HMR as per: https://webpack.js.org/api/hot-module-replacement#accept
if (module.hot) {
   module.hot.accept();
}